// Copyright 1999-2026. WebPros International GmbH. All rights reserved.

import classNames from 'classnames';
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown, Menu, MenuItem, setLocale, useTranslate } from '@plesk/ui-library';

import './LocaleSwitcher.less';

const LocaleSwitcher = ({ locales, value: defaultValue, onChange, appearance = 'default' }) => {
    const [value, setValue] = useState(defaultValue);
    const translate = useTranslate('components.common.LocaleSwitcher');

    const sortedLocales = useMemo(() => locales.slice().sort((a, b) => {
        if (b.code === 'en-US' || a.name > b.name) {
            return 1;
        }
        if (a.code === 'en-US' || b.name > a.name) {
            return -1;
        }
        return 0;
    }), [locales]);

    const handleChange = value => {
        setLocale(value);
        setValue(value);
        onChange(value);
    };

    return (
        <Dropdown
            menuPlacement="bottom-end"
            menu={(
                <Menu
                    className="locale-switcher-menu"
                >
                    <MenuItem
                        active={value === 'default'}
                        onClick={() => handleChange('default')}
                    >
                        {translate('defaultLocale')}
                    </MenuItem>
                    {sortedLocales.map(({ code, name, isRtl }) => (
                        <MenuItem
                            key={code}
                            active={value === code}
                            onClick={() => handleChange(code)}
                            dir={isRtl ? 'rtl' : 'ltr'}
                        >
                            {name}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        >
            <Button
                className={classNames(['locale-switcher-button', { 'pul-button--on-dark': appearance === 'dark' }])}
                tooltip={translate('localeLabel')}
                tooltipAsLabel
                icon="globe"
                ghost
                caret
            />
        </Dropdown>
    );
};

LocaleSwitcher.propTypes = {
    appearance: PropTypes.oneOf(['default', 'dark']),
    locales: PropTypes.arrayOf(PropTypes.shape({
        code: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        isRtl: PropTypes.bool.isRequired,
    })).isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

export default LocaleSwitcher;
